import { MediaType } from "../../types/mediaType";
import { ComfirmListing, Features, ListingType } from "../../types/userListing";
import type { Images } from "../../types/listingImages";
import type { ListingVideos } from "../../types/listingVideos";

export interface MLSFeatures {
    accessibility?: boolean;
    accessibilityType?: string;
    appliances?: string;
    architecturalStyle?: string;
    associationAmenities?: string;
    associationFeeIncludes?: string;
    basement?: boolean;
    basementType?: string;
    communityFeatures?: string;
    cooling?: boolean;
    coolingType?: string;
    fireplace?: boolean;
    fireplaceFeatures?: string;
    flooring?: string;
    garage?: boolean;
    garageSpaces?: number;
    heating?: boolean;
    heatingType?: string;
    interiorFeatures?: string;
    laundryFeatures?: string;
    patioAndPorchFeatures?: string;
    roof?: string;
    sewer?: string;
    spa?: boolean;
    spaFeatures?: string;
    view?: boolean;
    viewType?: string;
    waterfront?: boolean;
    waterfrontFeatures?: string;
    windowFeatures?: string;
}

export type AllFeatureKeys = keyof Features | keyof MLSFeatures;

export interface ListingFeaturesProps {
    propertyData: ComfirmListing;
    featureKeys: AllFeatureKeys[];
    isEditing: boolean;
}

export interface Progress {
    currentStep: string;
    imagesProcessed: number;
    totalImages: number;
    videosProcessed: number;
    totalVideos: number;
}

export interface DragAndDropMediaProps {
    type: MediaType;
    listingId?: string;
    onUploadStart?: () => void;
    onUploadComplete?: () => void;
    propertyAddress1?: string;
    mlsApproved?: boolean;
    assetType?: string;
    isUploading: boolean;
    setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
    listingType?: ListingType;
    isAdhoc?: boolean;
    onPendingFilesChange?: (count: number) => void;
    isSyncing?: boolean;
    hideNotes?: boolean;
    hideRenewalNote?: boolean;
    onActiveUploadsChange?: (count: number) => void;
    isActive?: boolean; // Whether this component's tab is currently active
    existingMedia?: { images?: Images[]; videos?: ListingVideos[] }; // Existing media from backend to show in completed grid
    onImageTagLimitExceeded?: () => void;
}

export enum UploadStatus {
    PENDING = "pending",
    VALIDATING = "validating",
    GETTING_SIGNED_URL = "getting_signed_url",
    UPLOADING = "uploading",
    PROCESSING = "processing",
    PERSISTING = "persisting",
    COMPLETED = "completed",
    FAILED = "failed",
    CANCELLED = "cancelled",
    RETRYING = "RETRYING",
}

export enum UploadPhase {
    VALIDATION = "validation",
    SIGNED_URL = "signed_url",
    UPLOAD = "upload",
    PERSISTENCE = "persistence",
    PROCESSING = "processing",
}

export interface FileUploadState {
    id: string;
    file: File;
    preview: string;
    status: UploadStatus;
    progress: number;
    phase: UploadPhase;
    uploadedBytes?: number;
    totalBytes?: number;
    uploadSpeed?: number;
    timeRemaining?: number;
    error?: string;
    signedUrl?: string;
    filename?: string;
    retryCount: number;
    needsConversion?: boolean;
    thumbnail?: string;
    abortController?: AbortController;
    startTime?: number;
    lastProgressTime?: number;
    uploadedUrl?: string;
    isExisting?: boolean; // Flag to identify pre-existing backend media
    retryAttempt?: number;
}

export interface UploadQueueState {
    files: FileUploadState[];
    activeImageUploads: number;
    activeVideoUploads: number;
    completedUploads: number;
    failedUploads: number;
    totalProgress: number;
}

export interface UploadConfig {
    maxConcurrentImageUploads: number;
    maxConcurrentVideoUploads: number;
    maxRetries: number;
    retryDelayBase: number;
    chunkSize?: number;
    enableParallelUploads: boolean;
}
